import React from 'react';
import { MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';

import { ValueContainerProps } from 'react-select/src/components/containers';
import { ControlProps } from 'react-select/src/components/Control';
import { MenuProps, NoticeProps } from 'react-select/src/components/Menu';
import { OptionProps } from 'react-select/src/components/Option';
import { PlaceholderProps } from 'react-select/src/components/Placeholder';
import { SingleValueProps } from 'react-select/src/components/SingleValue';
import { Chip } from '@mui/material';
import { CommonProps } from 'react-select/src/types';
import classNames from 'classnames';
import { useTranslation } from 'services/localization/useTranslation';
import { PurchaseType, PurchaseTypes } from 'common/types';
import {
	InventoryStatuses,
	getInventoryStatusLabel,
	AllocationOption,
	InventoryStatus,
} from 'common/modules/inventory';

interface OptionType {
	label: string;
	value: string;
}

function NoOptionsMessage(props: NoticeProps<OptionType, boolean>) {
	const { t } = useTranslation();
	return (
		<Typography
			color="textSecondary"
			className={props.selectProps.classes.noOptionsMessage}
			{...props.innerProps}
		>
			{t('idNotFound', 'No codes found')}
		</Typography>
	);
}

function LoadingMessage(props: CommonProps<OptionType, boolean>) {
	const { t } = useTranslation();
	return (
		<Typography color="textSecondary" className={props.selectProps.classes.noOptionsMessage}>
			{t('common:states.loading') + '...'}
		</Typography>
	);
}

const InputComponent = React.forwardRef(function (props, ref) {
	return <div {...props} />;
});

function Control(props: ControlProps<OptionType, boolean>) {
	const {
		children,
		innerProps,
		innerRef,
		selectProps: { classes, TextFieldProps, error },
	} = props;
	return (
		<TextField
			fullWidth
			error={error}
			label="ID"
			variant="outlined"
			InputProps={{
				inputComponent: InputComponent,
				inputProps: {
					className: classes.input,
					ref: innerRef,
					children,
					...innerProps,
				},
			}}
			{...TextFieldProps}
		/>
	);
}

function Option(props: OptionProps<OptionType, boolean>) {
	const classes = props.selectProps.classes;
	const isOccupied = Boolean(props.data.isOccupied);
	const allocationTypes = props.data.allocationTypes as AllocationOption[];
	const status = props.data.status as InventoryStatus;
	const purchaseType = props.selectProps.purchaseType as PurchaseType;
	const label = props.label;
	const { t } = useTranslation();
	const errorStatuses = Object.keys(InventoryStatuses).filter((status) => status !== 'IN_USE');

	const getPurchaseTypeCaption = (purchaseType: PurchaseType) => {
		switch (PurchaseTypes[purchaseType]) {
			case PurchaseTypes.rental:
				return t('orders.notForRent', 'Not for rent');
			case PurchaseTypes.sales:
				return t('orders.notForSale', 'Not for sale');
			default:
				return;
		}
	};

	return (
		<MenuItem
			ref={props.innerRef}
			selected={props.isSelected || props.isFocused}
			component="div"
			style={{
				fontWeight: props.isSelected ? 500 : 400,
			}}
			disableGutters
			{...props.innerProps}
		>
			<Stack direction="column" sx={{ width: '100%' }}>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					sx={{ padding: '0 8px' }}
				>
					<span className={classes.primaryOption}>{label}</span>
					{errorStatuses.includes(status) ? (
						<Typography
							variant="caption"
							className={classNames(classes.informationOption, classes.errorColor)}
						>
							{getInventoryStatusLabel(status, t)}
						</Typography>
					) : isOccupied ? (
						<Typography
							variant="caption"
							className={classNames(classes.informationOption, classes.warningColor)}
						>
							{t('inUse', 'In use')}
						</Typography>
					) : null}
				</Stack>
				{!allocationTypes.includes(purchaseType) && (
					<>
						<Typography variant="caption" className={classes.purchaseTypeCaption}>
							{getPurchaseTypeCaption(purchaseType)}
						</Typography>
					</>
				)}
			</Stack>
		</MenuItem>
	);
}

function IndicatorsContainer() {
	return null;
}

function Placeholder(props: PlaceholderProps<OptionType, boolean>) {
	return (
		<Typography
			color="textSecondary"
			className={props.selectProps.classes.placeholder}
			{...props.innerProps}
		>
			{props.children}
		</Typography>
	);
}

function SingleValue(props: SingleValueProps<OptionType>) {
	const selectProps = props.selectProps;
	const { occupied, outOfUse, isDisabled, wrongPurchaseType } = selectProps;
	const classes = selectProps.classes;
	return props.children ? (
		<Chip
			label={props.children}
			color="primary"
			classes={{
				root: classNames(classes.chip, {
					[classes.warningBg]: occupied || wrongPurchaseType,
					[classes.errorBg]: outOfUse,
					[classes.disabledBg]: isDisabled,
				}),
				label: props.selectProps.classes.chipLabel,
			}}
		/>
	) : null;
}

function ValueContainer(props: ValueContainerProps<OptionType, boolean>) {
	return (
		<div className={props.selectProps.classes.valueContainer}>
			<div className={props.selectProps.classes.valueWrapper}>{props.children}</div>
		</div>
	);
}

function Menu(props: MenuProps<OptionType, boolean>) {
	return (
		<Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
			{props.children}
		</Paper>
	);
}

function GroupHeading(props: any) {
	return <div className={props.selectProps.classes.groupHeading}>{props.children}</div>;
}

export const components = {
	Control,
	Menu,
	NoOptionsMessage,
	Option,
	Placeholder,
	SingleValue,
	ValueContainer,
	IndicatorsContainer,
	LoadingMessage,
	GroupHeading,
};
